import {
  Box,
  Flex,
  Input,
  BoxProps,
  FlexProps,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
} from '@chakra-ui/react';
import { MaskitoOptions, maskitoTransform } from '@maskito/core';
import { maskitoNumberOptionsGenerator } from '@maskito/kit';
import { useMaskito } from '@maskito/react';
import React, { RefCallback } from 'react';
import {
  RegisterOptions,
  useWatch,
  useFormContext,
  Controller,
  FieldValues,
  FieldPath,
  PathValue,
} from 'react-hook-form';

import { date as commonDate } from '@finley-technologies/common';

import { ResetIconButton } from 'packages/common-frontend/components/IconButton';
import {
  Select,
  SelectOption,
} from 'packages/common-frontend/components/Select';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { Typography } from 'packages/common-frontend/components/Typography';
import { colors } from '~/packages/common-frontend/colors';

const highlightedStyle = {
  backgroundColor: colors.blue[100],
  borderColor: colors.blue[400],
  borderWidth: '2px',
  borderRadius: '0.5rem',
};

type MaskTypes = 'usd' | 'none';

const USDOptions: MaskitoOptions = {
  ...maskitoNumberOptionsGenerator({
    decimalZeroPadding: true,
    precision: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    min: 0,
    prefix: '$',
  }),
};

const optionsMap = new Map<MaskTypes, MaskitoOptions>([['usd', USDOptions]]);

const getTextInputDefaultRules = <T extends FieldValues>(
  mask: MaskTypes,
): RegisterOptions<T, FieldPath<T>> | undefined => {
  if (mask === 'usd') {
    return {
      pattern: {
        value: /^\$\d+/, // This regex prevents react-hook-form from interpreting '$' as a valid value.
        message: 'Please enter a valid dollar amount.',
      },
    };
  }
  return undefined;
};

type TextInputProps<T extends FieldValues> = {
  name: FieldPath<T>;
  label?: string;
  placeholder?: string;
  defaultValue?: PathValue<T, FieldPath<T>>;
  mask?: MaskTypes;
  rules?: RegisterOptions<T, FieldPath<T>>;
  onReset?: () => void;
  helperText?: React.ReactNode;
  readOnly?: boolean;
  showErrorMessage?: boolean;
};

export const TextInput = <T extends FieldValues>({
  name,
  label,
  placeholder = '',
  defaultValue,
  rules,
  onReset,
  mask = 'none',
  helperText = '',
  readOnly = false,
  showErrorMessage = true,
}: TextInputProps<T>) => {
  const { control, resetField, trigger } = useFormContext<T>();

  const options = optionsMap.get(mask);
  const maskitoRef = useMaskito({ options });
  const defaultRules = getTextInputDefaultRules<T>(mask);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={{ ...defaultRules, ...rules }}
      render={({ field, fieldState: { error, isDirty } }) => {
        const ref: RefCallback<HTMLElement | null> = (node): void => {
          field.ref(node);
          maskitoRef(node);
        };

        // onChange is left undefined below per the Maskito docs: https://maskito.dev/frameworks/react
        // From the Docs:
        // Maskito core is developed as framework-agnostic library. It does not depend on any JS-framework's peculiarities.
        // It uses only native browser API. That is why you should use native onInput instead of React-specific onChange event.
        // Do not worry, both events works similarly! Read more about it in the official React documentation (https://react.dev/reference/react-dom/components/input#props).
        const maskitoField = {
          ...field,
          onChange: undefined,
          ref,
          onInput: (e: React.ChangeEvent<HTMLInputElement>): void => {
            const inputVal = e.target.value;
            switch (mask) {
              case 'usd': {
                const numValue = parseFloat(inputVal.replace(/[$,]/g, ''));
                if (Number.isNaN(numValue)) {
                  field.onChange(0);
                } else {
                  field.onChange(numValue);
                }
                break;
              }
              default:
                field.onChange(inputVal);
                break;
            }
          },
          value:
            field.value !== undefined && options
              ? maskitoTransform(String(field.value), options)
              : field.value ?? '',
        };

        return (
          <FormControl isInvalid={!!error} onBlur={field.onBlur}>
            {label && <FormLabel fontWeight="bold">{label}</FormLabel>}
            <Flex w="full" alignItems="center">
              <Box w="full">
                <Input
                  type="text"
                  placeholder={placeholder}
                  isDisabled={readOnly}
                  style={readOnly && isDirty ? highlightedStyle : undefined}
                  {...maskitoField}
                />
              </Box>
              {isDirty && !readOnly ? (
                <ResetIconButton
                  ariaLabel={`Reset ${name}`}
                  onClick={() => {
                    resetField(name, { keepDirty: false, keepError: false });
                    onReset?.();
                    trigger();
                  }}
                />
              ) : (
                <Box h="10" w="10" />
              )}
            </Flex>
            {showErrorMessage && (
              <FormErrorMessage>{error?.message}</FormErrorMessage>
            )}
            <FormHelperText>{helperText}</FormHelperText>
          </FormControl>
        );
      }}
    />
  );
};

export const SelectInput = ({
  name,
  options,
  placeholder = '',
  isDisabled = false,
}: {
  name: string;
  options: SelectOption[];
  placeholder?: string;
  isDisabled?: boolean;
}) => {
  const { control, setValue, getFieldState, resetField } = useFormContext();
  const value = useWatch({ control, name });

  return (
    <>
      <Select
        placeholderLabel={placeholder}
        value={value}
        onChange={(nextValue) => {
          setValue(name, nextValue, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }}
        options={options}
        isDisabled={isDisabled}
      />
      {getFieldState(name).isDirty && (
        <ResetIconButton
          ariaLabel={`Reset ${name}`}
          onClick={() =>
            resetField(name, { keepDirty: false, keepError: false })
          }
        />
      )}
    </>
  );
};

export const DateInput = ({
  name,
  placeholder,
  minDate,
  rules,
  readOnly = false,
  isSelectedDateOptional = false,
  stateFormat = 'fullDateStr',
  dateBoxProps,
  containerFlexProps,
}: {
  name: string;
  minDate?: Date;
  placeholder?: string;
  rules?: RegisterOptions;
  readOnly?: boolean;
  isSelectedDateOptional?: boolean;
  stateFormat?: 'fullDateStr' | 'date';
  dateBoxProps?: BoxProps;
  containerFlexProps?: FlexProps;
}) => {
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { control, resetField } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { isDirty } }) => (
        <Flex alignItems="center" h="40px" {...containerFlexProps}>
          <Box {...dateBoxProps}>
            {/* TODO: add focus styling for date picker when is dirty */}
            <SimpleDatePicker
              minDate={minDate}
              placeHolder={placeholder}
              dateFormat="MMMM dd, yyyy"
              selectedDate={field.value}
              setSelectedDate={(date: Date | null) => {
                if (!date) {
                  field.onChange(null);
                  return;
                }

                switch (stateFormat) {
                  case 'fullDateStr':
                    field.onChange(
                      commonDate.dateToFullDateString(date, clientTimeZone),
                    );
                    break;
                  case 'date':
                    field.onChange(date);
                    break;
                  default:
                    break;
                }
              }}
              width="full"
              disabled={readOnly}
              isSelectedDateOptional={isSelectedDateOptional}
              containerStyles={isDirty ? highlightedStyle : undefined}
            />
          </Box>
          {isDirty && !readOnly ? (
            <ResetIconButton
              ariaLabel={`Reset ${name}`}
              onClick={() =>
                resetField(name, { keepDirty: false, keepError: false })
              }
            />
          ) : (
            <Box h="10" w="10" />
          )}
        </Flex>
      )}
    />
  );
};

export const InputContainer = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Flex w="100%" gap="1rem" flexDirection="column" alignItems="flex-start">
    <Typography fontWeight="bold">{label}</Typography>
    {children}
  </Flex>
);
